import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LegalWorkspace from "@/pages/LegalWorkspace";
import WorkspaceHome from "@/pages/WorkspaceHome";
import CaseWorkspace from "@/pages/CaseWorkspace";
import Landing from "@/pages/Landing";
import HowItWorks from "@/pages/HowItWorks";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import { LangProvider, useLang } from "@/lib/lang";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";

const ADL_TITLE = "Adl | عدل";
const ADL_TITLE_AR = "عدل | Adl";

function HtmlDirSync() {
  const { lang, dir } = useLang();
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
    document.title = lang === "ar" ? ADL_TITLE_AR : ADL_TITLE;
  }, [lang, dir]);
  return null;
}

function BrandingGuard() {
  const { lang } = useLang();

  useEffect(() => {
    const title = lang === "ar" ? ADL_TITLE_AR : ADL_TITLE;

    const enforce = () => {
      if (/emergent/i.test(document.title)) {
        document.title = title;
      }
      document.getElementById("emergent-badge")?.remove();
      document.querySelectorAll('a[href*="emergent.sh"], a[href*="emergentagent.com"]').forEach((el) => {
        if (/made with emergent/i.test(el.textContent || "")) el.remove();
      });
    };

    document.title = title;
    enforce();

    const titleEl = document.querySelector("title");
    const titleObserver = titleEl
      ? new MutationObserver(enforce)
      : null;
    titleObserver?.observe(titleEl, { childList: true, characterData: true, subtree: true });

    const domObserver = new MutationObserver(enforce);
    domObserver.observe(document.documentElement, { childList: true, subtree: true });

    return () => {
      titleObserver?.disconnect();
      domObserver.disconnect();
    };
  }, [lang]);

  return null;
}

function AuthLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-loading">
      <Loader2 size={20} className="animate-spin text-slate-900" />
    </div>
  );
}

function LandingRoute() {
  const { user, loading } = useAuth();
  if (loading) return <AuthLoading />;
  if (user) return <Navigate to="/workspace" replace />;
  return <Landing />;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <AuthLoading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <div className="App">
          <HtmlDirSync />
          <BrandingGuard />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<LandingRoute />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/login" element={<Login />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route
                path="/workspace/cases/:caseId"
                element={
                  <ProtectedRoute>
                    <CaseWorkspace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/workspace/:section"
                element={
                  <ProtectedRoute>
                    <LegalWorkspace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/workspace"
                element={
                  <ProtectedRoute>
                    <WorkspaceHome />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-center" richColors />
        </div>
      </AuthProvider>
    </LangProvider>
  );
}

export default App;
